import { useState } from 'react';

import { setLocation } from 'js/lib/coursera.redirect';

import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { canEnrollThroughFreemium, enrollThroughFreemium } from 'bundles/enroll-common/utils/freemiumModelUtils';
import { MODAL_TYPES } from 'bundles/finaid-common/constants/openAidModalTypes';
import type { OpenAidModalType } from 'bundles/finaid-common/constants/openAidModalTypes';
import type { CourseTypeMetadataV1 } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import { ProfessionalCertificateS12n } from 'bundles/s12n-common/constants/s12nProductVariants';

export type UseOpenAidModalProps = {
  courseId: string;
  course?: CoursesV1;
  enrollmentAvailableChoices?: EnrollmentChoices;
  courseTypeMetadataWithVersion?: CourseTypeMetadataV1;
  productVariant?: string;
  isSpecialization?: boolean;
  s12nId?: string;
  toggleEnrollModal: () => void;
};

export type UseOpenAidModalResult = {
  openAidModalType: OpenAidModalType;
  canEnrollThroughPreview: boolean | undefined;
  isPreviewLoading: boolean;
  handleClickPreview: () => void;
};

/**
 * Owns everything the Open Financial Aid application modal needs: which variant
 * to show (Course / Specialization / Professional Certificate) and the
 * freemium-preview path — eligibility, in-flight loading state, and the click
 * handler that enrolls through freemium and redirects to the course home. When
 * the learner is not preview-eligible, `handleClickPreview` falls back to
 * opening the enroll modal.
 */
export const useOpenAidModal = ({
  courseId,
  course,
  enrollmentAvailableChoices,
  courseTypeMetadataWithVersion,
  productVariant,
  isSpecialization,
  s12nId,
  toggleEnrollModal,
}: UseOpenAidModalProps): UseOpenAidModalResult => {
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const getOpenAidModalType = (): OpenAidModalType => {
    if (productVariant === ProfessionalCertificateS12n) {
      return MODAL_TYPES.PROFESSIONAL_CERTIFICATE;
    }
    if (isSpecialization && s12nId) {
      return MODAL_TYPES.SPECIALIZATION;
    }
    return MODAL_TYPES.COURSE;
  };

  const canEnrollThroughPreview =
    enrollmentAvailableChoices &&
    courseTypeMetadataWithVersion?.courseTypeMetadata &&
    canEnrollThroughFreemium({
      courseId,
      enrollmentAvailableChoices,
      courseTypeMetadataWithVersion,
    });

  const handleClickPreview = () => {
    if (canEnrollThroughPreview) {
      setIsPreviewLoading(true);
      enrollThroughFreemium(courseId)
        .then(() => {
          setIsPreviewLoading(false);
          const courseSlug = course?.slug;
          if (courseSlug) {
            return setLocation(`/learn/${courseSlug}/home/welcome`);
          }
          return setLocation('/');
        })
        .catch(() => {
          setIsPreviewLoading(false);
        });
    } else {
      toggleEnrollModal();
    }
  };

  return {
    openAidModalType: getOpenAidModalType(),
    canEnrollThroughPreview,
    isPreviewLoading,
    handleClickPreview,
  };
};

export default useOpenAidModal;
